/**
 * Small async-data hooks shared by the app views (task 21.2).
 *
 * Views need two recurring shapes:
 *
 * - {@link useAsyncData} — load data on mount / when a key changes, exposing
 *   `{ data, error, loading, reload }`. Stale responses from a superseded load
 *   are dropped so a fast key change never renders an out-of-date result.
 * - {@link useAction} — run a user-triggered mutation, exposing
 *   `{ run, pending, error, reset }`, so buttons can disable while in flight
 *   and render the resulting error envelope inline.
 *
 * Both funnel failures to a caught value the {@link ErrorNotice} renders, so no
 * view has to hand-roll try/catch + loading state.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

/** State returned by {@link useAsyncData}. */
export interface AsyncData<T> {
  readonly data: T | undefined;
  readonly error: unknown;
  readonly loading: boolean;
  /** Re-run the loader (e.g. after a mutation elsewhere changes the data). */
  reload(): void;
}

/**
 * Load async data via `loader`, re-running whenever any value in `deps`
 * changes. Responses that arrive after a newer load started (or after unmount)
 * are ignored.
 */
export function useAsyncData<T>(
  loader: () => Promise<T>,
  deps: readonly unknown[],
): AsyncData<T> {
  const [data, setData] = useState<T | undefined>(undefined);
  const [error, setError] = useState<unknown>(undefined);
  const [loading, setLoading] = useState<boolean>(true);
  const requestId = useRef(0);
  const mounted = useRef(true);

  const run = useCallback(() => {
    const id = ++requestId.current;
    setLoading(true);
    setError(undefined);
    loader().then(
      (result) => {
        if (mounted.current && id === requestId.current) {
          setData(result);
          setLoading(false);
        }
      },
      (caught: unknown) => {
        if (mounted.current && id === requestId.current) {
          setError(caught);
          setLoading(false);
        }
      },
    );
    // `loader` is intentionally excluded from the dependency list: callers pass
    // an inline closure and control re-runs explicitly through `deps`.
  }, deps);

  useEffect(() => {
    mounted.current = true;
    run();
    return () => {
      mounted.current = false;
    };
  }, [run]);

  return { data, error, loading, reload: run };
}

/** State returned by {@link useAction}. */
export interface Action<Args extends unknown[], Result> {
  /** Invoke the action; resolves to the result or `undefined` on failure. */
  run(...args: Args): Promise<Result | undefined>;
  readonly pending: boolean;
  readonly error: unknown;
  /** Clear any error (e.g. when the user edits the form again). */
  reset(): void;
}

/**
 * Wrap a user-triggered async mutation with pending/error state. On success the
 * result resolves; on failure the caught value is stored for {@link ErrorNotice}
 * and the promise resolves to `undefined` so callers can branch simply.
 */
export function useAction<Args extends unknown[], Result>(
  action: (...args: Args) => Promise<Result>,
): Action<Args, Result> {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<unknown>(undefined);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const run = useCallback(
    async (...args: Args): Promise<Result | undefined> => {
      setPending(true);
      setError(undefined);
      try {
        const result = await action(...args);
        return result;
      } catch (caught) {
        if (mounted.current) {
          setError(caught);
        }
        return undefined;
      } finally {
        if (mounted.current) {
          setPending(false);
        }
      }
    },
    [action],
  );

  const reset = useCallback(() => setError(undefined), []);

  return { run, pending, error, reset };
}
